// English strings (source language): creations (workshop, store, wardrobe, previews, arcade games).
// The content of creations stays in its author's language; only the interface chrome is translated.

const en = {
  kinds: {
    workout: { label: 'Workout', plural: 'Workouts' },
    plan: { label: 'Plan', plural: 'Plans' },
    track: { label: 'Track', plural: 'Tracks' },
    mode: { label: 'Mode', plural: 'Modes' },
    game: { label: 'Game', plural: 'Games' },
    cosmetic: { label: 'Outfit', plural: 'Outfits' },
    item: { label: 'Item', plural: 'Items' },
    theme: { label: 'Mood', plural: 'Moods' },
  },
  slots: { jersey: 'jersey', frame: 'frame', helmet: 'helmet', wheels: 'wheels' },
  slotsPlural: { jersey: 'jerseys', frame: 'frames', helmet: 'helmets', wheels: 'wheels' },
  verbs: { workout: 'Ride', track: 'Ride', mode: 'Play', game: 'Play', plan: 'Start', cosmetic: 'Wear', item: 'Add to my races', theme: 'Pick this mood' },
  /** Labels of the key figures computed by the creations package (stored as computed by the server, so older ones are French). */
  factLabels: {
    Trees: 'Trees',
    Layers: 'Layers',
    Target: 'Target',
    'Team colour': 'Team colour',
    Duration: 'Duration',
    Elevation: 'Elevation',
    Slot: 'Slot',
    Finish: 'Finish',
    Rims: 'Rims',
    Delivery: 'Delivery',
    Mechanics: 'Mechanics',
    Weather: 'Weather',
    'Per week': 'Per week',
    'Max gradient': 'Max gradient',
    Weeks: 'Weeks',
    Sun: 'Sun',
    Workout: 'Workout',
    Workouts: 'Workouts',
    'TSS / week': 'TSS / week',
    Lap: 'Lap',
    Rule: 'Rule',
    Goal: 'Goal',
    // legacy: facts stored before English became the source language
    Arbres: 'Trees',
    Calques: 'Layers',
    Cible: 'Target',
    'Couleur d’équipe': 'Team colour',
    'Durée': 'Duration',
    'Dénivelé': 'Elevation',
    Emplacement: 'Slot',
    Finition: 'Finish',
    Jantes: 'Rims',
    Livraison: 'Delivery',
    'Mécaniques': 'Mechanics',
    'Météo': 'Weather',
    'Par semaine': 'Per week',
    'Pente max': 'Max gradient',
    Semaines: 'Weeks',
    Soleil: 'Sun',
    'Séance': 'Workout',
    'Séances': 'Workouts',
    'TSS / semaine': 'TSS / week',
    Tour: 'Lap',
    'Règle': 'Rule',
    But: 'Goal',
  } as Record<string, string>,
  official: 'Official',
  mine: 'My creation',
  owned: 'From the store',
  you: 'you',
  by: (name: string) => `by ${name}`,

  // Actions
  planStarted: (name: string) => `Plan “${name}” started 💪`,
  wearing: (name: string) => `You’re wearing “${name}”`,
  itemAdded: (name: string) => `“${name}” will drop from item boxes in your next races`,
  themeChosen: (name: string) => `Mood “${name}” selected`,

  // Importing a GPX trace as a track
  gpx: {
    import: 'Import a GPX',
    help: 'A GPX trace (Strava, Garmin, Komoot…) becomes a track: real route, elevation profile and gradients.',
    tooBig: 'File too large: 15 MB at most.',
    unreadable: 'Unreadable file: this is not a valid GPX.',
    none: 'No usable trace in this file.',
    desc: (km: number, climb: number) => `Imported from a GPX · ${km.toFixed(1)} km · ${climb} m of climbing`,
    imported: (file: string) => `Track imported from “${file}”. Adjust the name, laps or mood, then try it.`,
    oneWay: (km: number) =>
      `The trace does not return to its start: the track rides the ${km.toFixed(1)} km out, then back along a copy of the same road laid out alongside.`,
    noElevation: 'The file has no elevation: the track is flat.',
    chooseTitle: (n: number) => `${n} traces in this file`,
    chooseIntro: 'Each trace becomes a track. Open one in the workshop to tweak it, or save every valid one to your drafts.',
    loop: 'Loop',
    oneWayBadge: 'Out and back',
    open: 'Open in the workshop',
    saveAll: (n: number) => (n > 1 ? `Save the ${n} valid tracks` : 'Save the valid track'),
    savedAll: (n: number) => (n > 1 ? `${n} tracks saved to your drafts` : 'Track saved to your drafts'),
  },

  // Workshop
  workshop: {
    title: 'Workshop',
    intro:
      'Describe what you want in a few sentences: a workout, a plan, a track, a race mode, an arcade game or a jersey. You can then tweak it, try it and share it.',
    newCreation: 'New creation',
    auto: 'Auto',
    ideas: {
      auto: [
        'A tower defense where my watts power the towers',
        'A 45-minute workout to improve my sprint without burning out',
        'A mode where the last rider of each lap becomes the hunter',
        'A retro 80s jersey in blue and orange with a lightning bolt',
      ],
      workout: [
        'A 45-minute workout to improve my sprint without burning out',
        '1 hour of endurance with surges every 10 minutes',
        '4×8 min threshold for a hilly gran fondo',
      ],
      plan: [
        'Get ready for a 120 km gran fondo in 10 weeks, Tuesday, Thursday and weekends, 1 hour on weekdays',
        'Get back on the bike in 4 weeks, 3 rides of 45 minutes',
        'Gain 20 W of FTP in 8 weeks, 5 hours a week',
      ],
      track: [
        'A night criterium between glass towers, with a steel bridge over the river',
        'A cobbled wall through an old town, then down to a lake in the vineyards',
        'An alpine pass with a tunnel, a stone bridge and a sprint through a village',
        'A flat seafront loop with palm trees, a port and a lighthouse',
        'A neon road on Mars, past a rocket and through red canyons',
      ],
      mode: ['The last rider of each lap hunts the others', 'Missile race: only missiles and shields', 'Team time trial with no drafting'],
      game: [
        'A tower defense where my watts power the towers',
        'A rhythm game where you hold the cadence to the music',
        'Escaping a zombie horde on the climbs',
      ],
      cosmetic: [
        'A retro 80s jersey in blue and orange with a lightning bolt',
        'A pearly purple frame with a gold fork',
        'Black disc wheels with red hubs',
        'A matte green helmet with a white stripe and mirrored visor',
      ],
      item: [
        'A mud puddle that slows down whoever rides through it for 5 s',
        'An umbrella that blocks the wind for 6 s',
        'A beehive that stings every rider ahead of you',
      ],
      theme: ['A summer night under the stars with a purple sky', 'A red canyon desert under a blazing sun', 'Rainy autumn in Flanders, low sky and cobbles'],
    },
    connecting: 'Connecting to the workshop…',
    aiOff: 'AI is not available on this server. You can still start from an example and edit the format by hand.',
    remaining: (n: number) => (n === 1 ? '1 AI creation left today' : `${n} AI creations left today`),
    placeholderEdit: 'What to change: “harder”, “shorten the climb”, “add bananas”…',
    placeholderNew: 'What you want to create…',
    fixGame: (err: string) => `Fix the game: ${err}`,
    busy: '⏳ Creating…',
    retouch: 'Tweak',
    create: 'Create',
    fromExample: 'Or start from an example:',
    draftSaved: 'Draft saved',
    notSaved: 'Not saved',
    name: 'Name',
    description: 'Description',
    showFormat: '{ } Edit the format',
    hideFormat: 'Hide the format',
    publish: 'Publish',
    apply: 'Apply',
    formatHelp: (kind: string) => `The format (${kind}) is validated on every change.`,
    emptyPreview: 'Your creation’s preview will appear here.',
    myDrafts: 'My drafts',
    published: 'published',
    trialRunning: '⏳ Running a fast-forward test game…',
    trialOk: (min: number, ended: boolean, score: number) => `✓ Test passed: ${min} min simulated${ended ? ', game finished' : ''}, score ${score}`,
    trialStillBroken: (err: string) => `✗ The game still crashes: ${err}`,
    trialFixing: (err: string) => `The test failed (${err}). Fixing it…`,
    fixApplied: 'Fix applied.',
    done: 'Here you go!',
    formatApplied: 'Format applied',
    formatAppliedErrors: 'Format applied, with errors',
    invalidJson: (err: string) => `Invalid JSON: ${err}`,
    publishTitle: (name: string) => `Publish “${name}”`,
    newVersion: 'A new version will be published; earlier ones stay playable.',
    joinsLibrary: 'Your creation joins the store, where every player can find it.',
    visibility: 'Visibility',
    vis: { public: 'Public', unlisted: 'Link only', private: 'Private' },
    price: 'Store price (coins, 0 = free)',
    priceHelp: 'You get half the price on every purchase.',
    publishedToast: (v: number) => `Published (version ${v}) 🎉`,
    deleteDraft: (name: string) => `Delete the draft “${name}”?`,
  },

  // Store (ADR 0017): official and community content in one place; the collection is what the player added
  store: {
    title: 'Store',
    intro: 'Everything you can play or wear, official and made by players: modes, tracks, workouts, plans, items, moods, outfits and games. Add what you like to your collection; your screens show only that.',
    create: 'Create',
    all: 'All',
    search: 'Search the store…',
    sort: { top: 'Top rated', new: 'Newest', plays: 'Most played' },
    sources: { all: 'Everything', official: 'Official', community: 'Community', mine: 'My collection' },
    coinsHelp: 'Coins are earned by riding: one every 10 kJ, ten per achievement. Sold outfits cost coins; everything else is free.',
    add: 'Add',
    added: 'In your collection',
    addedToast: (name: string) => `“${name}” is in your collection`,
    remove: 'Remove from my collection',
    removedToast: (name: string) => `“${name}” left your collection`,
    buy: (price: number) => `Buy for ${price}`,
    bought: 'Bought! It is in your collection and on your rider.',
    level: (n: number) => `Level ${n}`,
    unlocksAt: (n: number) => `Unlocks at level ${n}: keep riding.`,
    unreachable: (err: string) => `Community content unreachable: ${err}`,
    empty: 'Nothing matches here.',
    emptyMine: 'Your collection is empty for this filter.',
    browse: 'Browse the store',
    draft: 'Draft',
    ratings: (n: number) => (n === 1 ? '1 rating' : `${n} ratings`),
    by: (name: string) => `By ${name}`,
    version: (v: number) => `version ${v}`,
    remix: 'remix',
    madeWith: (prompt: string) => `made with the workshop: “${prompt}”`,
    rideThis: 'Ride this workout…',
    onTrack: 'on a track',
    inGame: (name: string) => `in the game “${name}”`,
    yourRating: 'Your rating:',
    report: 'Report',
    reportHelp: 'Report inappropriate content',
    remixIt: 'Remix in the workshop',
    reportTitle: (name: string) => `Report “${name}”`,
    reportIntro: 'A creation reported by several players is hidden from the store.',
    reason: 'Reason',
    reasons: { offensant: 'Offensive content', marque: 'Brand or real person', donnees: 'Personal data', triche: 'Cheating or broken game', autre: 'Other' },
    detail: 'Details (optional)',
    reported: 'Thanks, noted',
    rated: 'Thanks for rating!',
    /** Link shown by a picker whose collection has nothing of a kind. */
    findMore: (kind: string) => `Find more ${kind} in the <a href="#/store">store</a>.`,
    createOutfit: 'Create an outfit',
    worn: 'Worn',
    wear: 'Wear',
  },

  // Wardrobe
  wardrobe: {
    title: 'Wardrobe',
    intro: 'Your rider, as every player sees them in a race: face, calves, build and outfit. Pick what to change and the camera moves in on it. Spin them with the mouse.',
    teamColor: 'Team color',
    randomFace: 'Random face',
    wholeRider: 'Whole rider',
    store: 'Store',
    body: 'Body and face',
    skin: 'Skin',
    hair: 'Hair and beard',
    eyeColor: 'Eye color',
    calves: (word: string, k: string) => `Calves: ${word} (×${k})`,
    calfWords: ['slim', 'regular', 'sturdy', 'climber’s', 'legendary'],
    build: (k: string) => `Build (×${k})`,
    parts: { figure: 'Rider', hairstyle: 'Haircut', eyes: 'Eyes', brows: 'Eyebrows', mouth: 'Mouth', beard: 'Beard', glasses: 'Glasses' },
    choices: {
      figure: { male: 'Man', female: 'Woman' },
      hairstyle: { short: 'Short', shaved: 'Shaved', ponytail: 'Ponytail', braid: 'Braid', long: 'Long' },
      eyes: { ronds: 'Round', amande: 'Almond', rieurs: 'Smiling', endormis: 'Sleepy', etoiles: 'Starry' },
      brows: { fins: 'Thin', epais: 'Thick', fronces: 'Frowning', arques: 'Arched' },
      mouth: { sourire: 'Smile', neutre: 'Neutral', grimace: 'Effort grimace', langue: 'Tongue out', dents: 'Gritted teeth' },
      beard: { aucune: 'Clean-shaven', moustache: 'Moustache', bouc: 'Goatee', barbe: 'Beard', favoris: 'Sideburns' },
      glasses: { aucune: 'None', sport: 'Sport', rondes: 'Round', miroir: 'Mirrored' },
    },
    freckles: 'Freckles',
    locked: (n: number) => `Level ${n}`,
    emptySlot: 'Nothing here yet.',
    more: (slot: string) =>
      `More ${slot} are waiting in the <a href="#/store/cosmetic" data-store-link>store</a>, or create your own in the <a href="#/workshop/create/cosmetic">workshop</a>.`,
  },

  // Previews
  preview: {
    weekShort: (n: number) => `W${n}`,
    days: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    notFound: 'not found',
    delivery: { instant: 'Instant effect', trap: 'Trap dropped behind you', missile: 'Homing missile' },
    during: (s: number) => `for ${s} s`,
    fx: {
      power: (k: number) => `power ×${k}`,
      drag: (k: number) => `drag ×${k}`,
      speed: (k: number) => `speed ×${k} on impact`,
      shield: (s: number) => `${s} s shield`,
      draft: (pct: number) => `${pct}% draft`,
      jam: 'gears jammed',
      spin: 'stunned',
      grade: (g: string) => `felt gradient ${g}%`,
      wind: (v: number) => `felt wind ${v} m/s`,
    },
    noEffect: 'no effect',
    blockable: 'Stopped by a shield',
    unblockable: 'Unblockable',
    odds: { back: 'drops mostly at the back of the race', front: 'drops mostly at the front', even: 'drops everywhere' },
    failed: (err: string) => `Preview unavailable: ${err}`,
  },

  // Arcade games
  game: {
    hint: '← → • Space • E/D • ↑↓ simulated watts',
    pauseTitle: 'Pause (Esc)',
    keys: (buttons: string) => `${buttons}: game buttons`,
    keysWatts: ' · <kbd>↑</kbd>/<kbd>↓</kbd> simulated watts',
    keysWorkout: ' · <kbd>+</kbd>/<kbd>−</kbd> workout intensity',
    quit: 'Finish and save',
    crashed: 'The game stopped',
    crashIntro: 'An error occurred in the game’s program:',
    fix: 'Fix in the workshop',
    leave: 'Quit',
    points: 'points',
    again: 'Play again',
  },
};
export default en;
